import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import PageHeader from '../../../components/layout/PageHeader';
import {
  Users, UserPlus, Briefcase, Target, ShieldCheck, TrendingUp,
  Loader2, Search, MoreVertical, Trash2, X, ChevronRight, LayoutGrid,
} from 'lucide-react';
import { getBackendBaseUrl } from '../../../lib/backendUrl';
import { useAdvisorStore } from '../../../state/advisorStore';
import { useAuthStore } from '../../../state/authStore';

const BACKEND = getBackendBaseUrl();

const RISK_COLORS = {
  Conservative: 'bg-blue-500/10 text-blue-600',
  Moderate: 'bg-emerald-500/10 text-emerald-600',
  Aggressive: 'bg-amber-500/10 text-amber-600',
  'Very Aggressive': 'bg-red-500/10 text-red-600',
};

function formatCorpus(v) {
  if (!v) return '—';
  if (v >= 10_000_000) return `₹${(v / 10_000_000).toFixed(1)} Cr`;
  if (v >= 100_000) return `₹${(v / 100_000).toFixed(1)} L`;
  return `₹${v.toLocaleString('en-IN')}`;
}

export default function ClientsPage() {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const {
    clients, clientsLoading, clientsError, fetchClients, createClient, deleteClient,
    modelPortfolios, modelPortfoliosLoading, fetchModelPortfolios,
    createModelPortfolio, deleteModelPortfolio,
    activeOrgId, setActiveOrgId,
    pendingProposals, pendingProposalsLoading, fetchPendingProposals,
    approveProposal, rejectProposal,
  } = useAdvisorStore();

  const [orgs, setOrgs] = useState([]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', investment_goal: '', corpus: '' });
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('clients');

  const [showAddMP, setShowAddMP] = useState(false);
  const [mpForm, setMpForm] = useState({ name: '', description: '', risk_bucket: 'Moderate', benchmark: 'NIFTY 50' });
  const [creatingMP, setCreatingMP] = useState(false);

  // Reject-proposal dialog (replaces window.prompt for rejection notes).
  const [rejectTarget, setRejectTarget] = useState(null); // proposal being rejected
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await rejectProposal(rejectTarget.id, rejectNotes.trim());
      if (activeOrgId) fetchPendingProposals(activeOrgId);
      setRejectTarget(null);
      setRejectNotes('');
    } finally {
      setRejecting(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${BACKEND}/api/orgs`).then(({ data }) => {
      setOrgs(Array.isArray(data) ? data : []);
    }).catch(() => setOrgs([]));
  }, [user?.id]);

  useEffect(() => {
    fetchClients();
    fetchModelPortfolios();
  }, [fetchClients, fetchModelPortfolios, activeOrgId]);

  useEffect(() => {
    if (activeTab === 'compliance' && activeOrgId) fetchPendingProposals(activeOrgId);
  }, [activeTab, activeOrgId, fetchPendingProposals]);

  const clientsList = Array.isArray(clients) ? clients : [];
  const filtered = clientsList.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  async function handleCreateClient(e) {
    e.preventDefault();
    setCreating(true);
    try {
      await createClient({ ...form, corpus: form.corpus ? parseFloat(form.corpus) : null });
      setForm({ name: '', email: '', phone: '', investment_goal: '', corpus: '' });
      setShowAddClient(false);
    } catch {}
    setCreating(false);
  }

  async function handleCreateMP(e) {
    e.preventDefault();
    setCreatingMP(true);
    try {
      await createModelPortfolio(mpForm);
      setMpForm({ name: '', description: '', risk_bucket: 'Moderate', benchmark: 'NIFTY 50' });
      setShowAddMP(false);
    } catch {}
    setCreatingMP(false);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Advisor — Clients"
        subtitle={`${clientsList.length} client${clientsList.length !== 1 ? 's' : ''}`}
        actions={
          <Link to="/public/research-board" className="text-xs text-emerald-600 hover:underline">Research board</Link>
        }
      />
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-muted-foreground">Book:</span>
        <select
          className="rounded-md border bg-background px-2 py-1.5 text-sm"
          value={activeOrgId || ''}
          onChange={e => setActiveOrgId(e.target.value || null)}
        >
          <option value="">Personal clients</option>
          {orgs.map(o => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </div>
      {clientsError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {clientsError}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-card/50 rounded-lg w-fit">
        {[{ id: 'clients', label: 'Clients', icon: Users }, { id: 'models', label: 'Model Portfolios', icon: LayoutGrid }, { id: 'compliance', label: 'Compliance', icon: ShieldCheck }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === t.id ? 'bg-emerald-500/10 text-emerald-500' : 'text-muted-foreground hover:text-foreground'}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {activeTab === 'clients' && (
        <>
          {/* Search + Add */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Button onClick={() => setShowAddClient(true)} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
              <UserPlus className="w-4 h-4 mr-1" />Add Client
            </Button>
          </div>

          {/* Add Client Form */}
          {showAddClient && (
            <Card className="border-emerald-500/30">
              <CardContent className="pt-4">
                <form onSubmit={handleCreateClient} className="space-y-3">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-semibold">New Client</h3>
                    <button onClick={() => setShowAddClient(false)}><X className="w-4 h-4 text-muted-foreground" /></button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <Input placeholder="Full Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                    <Input placeholder="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                    <Input placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                    <Input placeholder="Investment Goal" value={form.investment_goal} onChange={e => setForm(f => ({ ...f, investment_goal: e.target.value }))} />
                    <Input placeholder="Corpus (₹)" type="number" value={form.corpus} onChange={e => setForm(f => ({ ...f, corpus: e.target.value }))} />
                  </div>
                  <Button type="submit" size="sm" disabled={creating || !form.name} className="bg-emerald-600 hover:bg-emerald-700">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Create Client
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Client Grid */}
          {clientsLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
          ) : filtered.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No clients yet</p>
              <p className="text-sm mt-1">Add your first client to start managing their portfolio.</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(c => (
                <Card key={c.id} className="hover:border-emerald-500/30 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/public/clients/${c.id}`)}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500 font-semibold text-sm">
                          {c.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-sm">{c.name}</h3>
                          {c.email && <p className="text-xs text-muted-foreground">{c.email}</p>}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Briefcase className="w-3 h-3" />
                        <span>{formatCorpus(c.corpus)}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Target className="w-3 h-3" />
                        <span>{c.investment_goal || 'No goal set'}</span>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center gap-2">
                      {c.risk_bucket ? (
                        <Badge className={`text-[10px] ${RISK_COLORS[c.risk_bucket] || 'bg-gray-500/10 text-gray-600'}`}>
                          <ShieldCheck className="w-3 h-3 mr-0.5" />{c.risk_bucket}
                        </Badge>
                      ) : (
                        <Badge className="text-[10px] bg-gray-500/10 text-gray-500">No Risk Profile</Badge>
                      )}
                      {c.proposal_count > 0 && (
                        <Badge className="text-[10px] bg-purple-500/10 text-purple-600">
                          {c.proposal_count} proposal{c.proposal_count > 1 ? 's' : ''}
                        </Badge>
                      )}
                      {c.assigned_to && (
                        <Badge variant="outline" className="text-[10px]">Assigned</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'compliance' && (
        <div className="space-y-4">
          {!activeOrgId && (
            <p className="text-sm text-muted-foreground">Select an organization above to view pending proposals.</p>
          )}
          {activeOrgId && pendingProposalsLoading && (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-emerald-500" /></div>
          )}
          {activeOrgId && !pendingProposalsLoading && pendingProposals.length === 0 && (
            <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No proposals pending review.</CardContent></Card>
          )}
          {activeOrgId && pendingProposals.map(p => (
            <Card key={p.id}>
              <CardContent className="pt-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{p.client_name}</p>
                  <p className="text-xs text-muted-foreground">{p.summary?.slice(0, 120) || '—'}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-emerald-600" onClick={async () => { await approveProposal(p.id); fetchPendingProposals(activeOrgId); }}>Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => { setRejectTarget(p); setRejectNotes(''); }}>Reject</Button>
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/public/clients/${p.client_id}`)}>Open client</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'models' && (
        <>
          <div className="flex items-center gap-3">
            <Button onClick={() => setShowAddMP(true)} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
              <UserPlus className="w-4 h-4 mr-1" />New Model Portfolio
            </Button>
          </div>

          {showAddMP && (
            <Card className="border-emerald-500/30">
              <CardContent className="pt-4">
                <form onSubmit={handleCreateMP} className="space-y-3">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-semibold">New Model Portfolio</h3>
                    <button onClick={() => setShowAddMP(false)}><X className="w-4 h-4 text-muted-foreground" /></button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Input placeholder="Name *" value={mpForm.name} onChange={e => setMpForm(f => ({ ...f, name: e.target.value }))} required />
                    <Input placeholder="Description" value={mpForm.description} onChange={e => setMpForm(f => ({ ...f, description: e.target.value }))} />
                    <select value={mpForm.risk_bucket} onChange={e => setMpForm(f => ({ ...f, risk_bucket: e.target.value }))}
                      className="rounded-md border bg-background px-3 py-2 text-sm">
                      {['Conservative', 'Moderate', 'Aggressive', 'Very Aggressive'].map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                    <Input placeholder="Benchmark" value={mpForm.benchmark} onChange={e => setMpForm(f => ({ ...f, benchmark: e.target.value }))} />
                  </div>
                  <Button type="submit" size="sm" disabled={creatingMP || !mpForm.name} className="bg-emerald-600 hover:bg-emerald-700">
                    {creatingMP ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Create
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {modelPortfoliosLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
          ) : modelPortfolios.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              <LayoutGrid className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No model portfolios</p>
              <p className="text-sm mt-1">Create template allocations that can be applied to multiple clients.</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {modelPortfolios.map(mp => (
                <Card key={mp.id} className="group">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-sm">{mp.name}</h3>
                        {mp.description && <p className="text-xs text-muted-foreground mt-0.5">{mp.description}</p>}
                      </div>
                      <button onClick={() => deleteModelPortfolio(mp.id)} className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="w-3.5 h-3.5 text-red-400 hover:text-red-500" />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      {mp.risk_bucket && (
                        <Badge className={`text-[10px] ${RISK_COLORS[mp.risk_bucket] || ''}`}>
                          {mp.risk_bucket}
                        </Badge>
                      )}
                      <Badge className="text-[10px] bg-gray-500/10 text-gray-500">{mp.benchmark || 'NIFTY 50'}</Badge>
                      {mp.stock_allocation?.length > 0 && (
                        <Badge className="text-[10px] bg-purple-500/10 text-purple-600">
                          {mp.stock_allocation.length} stocks
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) { setRejectTarget(null); setRejectNotes(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject proposal</DialogTitle>
            <DialogDescription>
              {rejectTarget ? `Reject the proposal for ${rejectTarget.client_name}. Add optional notes for the record.` : ''}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            placeholder="Rejection notes (optional)"
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectNotes(''); }} disabled={rejecting}>Cancel</Button>
            <Button variant="destructive" onClick={confirmReject} disabled={rejecting}>
              {rejecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Reject proposal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
